"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type ChangeGroup,
  convertSpelling,
  groupChanges,
  type SpellingDirection,
  type SpellingResult,
} from "@/lib/tool-kit/text-analysis/spelling";
import type {
  AmbiguityNote,
  SpellingPattern,
} from "@/lib/tool-kit/text-analysis/spelling-data";

/** English strings. A translated content file overrides these through `ui`. */
export const BRITISH_AMERICAN_SPELLING_CONVERTER_STRINGS = {
  directionLabel: "Convert",
  ukToUs: "British → American",
  usToUk: "American → British",
  oxford:
    "Oxford spelling: keep -ize endings (organize, realize) with British forms otherwise",
  keepCapitalized:
    "Leave capitalized words inside a sentence alone — they are usually names (the Labour Party, Pearl Harbor)",
  textLabel: "Text to convert",
  placeholderUk:
    "Paste British English: colour, organise, centre, travelled, analyse…",
  placeholderUs:
    "Paste American English: color, organize, center, traveled, analyze…",
  trySample: "Try a sample",
  clear: "Clear",
  emptyPrompt: "Paste your text to convert it.",
  results: "Converted text",
  statChanged: "Words changed",
  statCheck: "Words to check",
  statKept: "Left unchanged",
  nothingUk: "No British spellings found to change.",
  nothingUs: "No American spellings found to change.",
  copyText: "Copy converted text",
  copied: "Copied!",
  download: "Download .txt",
  filename: "converted-text.txt",
  highlightLabel: "The converted text with every change highlighted",
  legendChanged: "Changed",
  legendCheck: "Check the meaning",
  legendKept: "Left unchanged",
  changesHeading: "Changes",
  changesHint:
    "Tick a word to keep its original spelling everywhere — for a quotation, a title, or a name.",
  colOriginal: "Original",
  colConverted: "Converted",
  colPattern: "Pattern",
  colTimes: "Times",
  colKeep: "Keep original",
  keepAria: "Keep “{word}” unchanged",
  patterns: {
    our: "-our / -or",
    re: "-re / -er",
    ise: "-ise / -ize",
    yse: "-yse / -yze",
    ogue: "-ogue / -og",
    ll: "doubled l (travelled)",
    l: "-l / -ll (enrol)",
    ence: "-ce / -se (defence)",
    ae: "ae, oe / e",
    other: "other spelling",
  } as Record<SpellingPattern, string>,
  checkHeading: "Check these by meaning",
  checkItem: "{word}: {note}",
  notes: {
    "noun-plural":
      "as a verb this becomes -yzes in American English; as a plural noun (the analyses) it stays the same.",
    draughts:
      "the board game is checkers in American English; the plural of draught (a current of air) is drafts.",
    practice:
      "British English writes practice for the noun and practise for the verb.",
    license:
      "British English writes licence for the noun and license for the verb.",
    meter:
      "a unit of length is a metre in British English; a measuring instrument is a meter.",
    program:
      "a computer program is spelled program in British English too; a schedule or broadcast is a programme.",
    tire: "the wheel is a tyre in British English; the verb (to tire) is spelled the same.",
    curb: "the edge of a pavement is a kerb in British English; the verb (to curb) is spelled the same.",
  } as Record<AmbiguityNote, string>,
  keptHeading: "Capitalized words left unchanged",
  keptHint:
    "Probably names or titles. If one is an ordinary word at the start of a heading, change it by hand.",
  keptItem: "{from} (would be {to})",
  sampleUk:
    "The research centre analysed the behaviour of 200 travellers. Participants were randomised, and the organisers recognised that colour cues favoured recall. The programme’s defence of its catalogue was, by any measure, a sensible manoeuvre — though the Labour Party’s licence review analyses it differently.",
  sampleUs:
    "The research center analyzed the behavior of 200 travelers. Participants were randomized, and the organizers recognized that color cues favored recall. The program needs a license, so the team practiced its defense of the catalog at the Centers for Disease Control.",
} as const;

type Strings = typeof BRITISH_AMERICAN_SPELLING_CONVERTER_STRINGS;

const TONES = { changed: "emerald", check: "amber", kept: "sky" } as const;

function highlightMatches(result: SpellingResult): TextMatch[] {
  return [
    ...result.changes.map((change) => ({
      start: change.outStart,
      end: change.outEnd,
      category: "changed",
      title: change.from,
    })),
    ...result.flags.map((flag) => ({
      start: flag.outStart,
      end: flag.outEnd,
      category: "check",
    })),
    ...result.kept.map((kept) => ({
      start: kept.outStart,
      end: kept.outEnd,
      category: "kept",
      title: kept.to,
    })),
  ];
}

function ChangeTable({
  groups,
  onToggle,
  strings,
}: {
  groups: ChangeGroup[];
  onToggle: (word: string, keep: boolean) => void;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.changesHeading}</h3>
      <p className={HINT}>{strings.changesHint}</p>
      <div className="max-h-[420px] overflow-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.colOriginal}
              </th>
              <th className={TH} scope="col">
                {strings.colConverted}
              </th>
              <th className={TH} scope="col">
                {strings.colPattern}
              </th>
              <th className={TH} scope="col">
                {strings.colTimes}
              </th>
              <th className={TH} scope="col">
                {strings.colKeep}
              </th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.from}>
                <td className={TD}>{group.from}</td>
                <td className={`${TD} font-medium`}>{group.to}</td>
                <td className={`${TD} text-muted-foreground`}>
                  {strings.patterns[group.pattern]}
                </td>
                <td className={TD}>{fmt(group.count)}</td>
                <td className={TD}>
                  <Checkbox
                    checked={group.kept}
                    label={
                      <span className="sr-only">
                        {t(strings.keepAria, { word: group.from })}
                      </span>
                    }
                    onChange={(keep) => onToggle(group.from, keep)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Results({
  result,
  direction,
  onToggle,
  strings,
}: {
  result: SpellingResult;
  direction: SpellingDirection;
  onToggle: (word: string, keep: boolean) => void;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const groups = groupChanges(result);
  const capitalized = result.kept.filter(
    (kept) => kept.reason === "capitalized"
  );
  const legend = [
    [TONES.changed, strings.legendChanged],
    [TONES.check, strings.legendCheck],
    [TONES.kept, strings.legendKept],
  ] as const;
  const nothing = direction === "uk-us" ? strings.nothingUk : strings.nothingUs;
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.statChanged}
          value={fmt(result.changes.length)}
        />
        <StatCard label={strings.statCheck} value={fmt(result.flags.length)} />
        <StatCard label={strings.statKept} value={fmt(result.kept.length)} />
      </StatGrid>
      {groups.length === 0 && result.flags.length === 0 ? (
        <Notice>{nothing}</Notice>
      ) : null}
      <ul className="flex flex-wrap gap-3 text-muted-foreground text-xs">
        {legend.map(([tone, label]) => (
          <li key={label}>
            <span className={HIGHLIGHT_TONES[tone]}>{label}</span>
          </li>
        ))}
      </ul>
      <HighlightedText
        label={strings.highlightLabel}
        matches={highlightMatches(result)}
        text={result.output}
        tones={TONES}
      />
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyText}
          text={result.output}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() => downloadText(strings.filename, result.output)}
          type="button"
        >
          {strings.download}
        </button>
      </div>
      {result.flags.length > 0 ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.checkHeading}</h3>
          <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
            {[
              ...new Map(
                result.flags.map((flag) => [flag.word.toLowerCase(), flag])
              ).values(),
            ].map((flag) => (
              <li key={flag.word.toLowerCase()}>
                {t(strings.checkItem, {
                  word: flag.word,
                  note: strings.notes[flag.note],
                })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {groups.length > 0 ? (
        <ChangeTable groups={groups} onToggle={onToggle} strings={strings} />
      ) : null}
      {capitalized.length > 0 ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.keptHeading}</h3>
          <p className={HINT}>{strings.keptHint}</p>
          <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
            {capitalized.map((kept) => (
              <li key={kept.start}>
                {t(strings.keptItem, { from: kept.from, to: kept.to })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

export default function BritishAmericanSpellingConverterWidget() {
  const strings = useToolUi(BRITISH_AMERICAN_SPELLING_CONVERTER_STRINGS);
  const [text, setText] = useState("");
  const [direction, setDirection] = useState<SpellingDirection>("uk-us");
  const [oxford, setOxford] = useState(false);
  const [keepCapitalized, setKeepCapitalized] = useState(true);
  const [keepWords, setKeepWords] = useState<ReadonlySet<string>>(new Set());
  const deferred = useDeferredValue(text);
  const result = useMemo(
    () =>
      deferred.trim()
        ? convertSpelling(deferred, {
            direction,
            oxford,
            keepCapitalized,
            keepWords,
          })
        : null,
    [deferred, direction, oxford, keepCapitalized, keepWords]
  );

  function toggleKeep(word: string, keep: boolean) {
    setKeepWords((current) => {
      const next = new Set(current);
      if (keep) {
        next.add(word);
      } else {
        next.delete(word);
      }
      return next;
    });
  }

  return (
    <>
      <PillGroup
        label={strings.directionLabel}
        name="bas-direction"
        onChange={(next) => {
          setDirection(next);
          setKeepWords(new Set());
        }}
        options={[
          { value: "uk-us", label: strings.ukToUs },
          { value: "us-uk", label: strings.usToUk },
        ]}
        value={direction}
      />
      <div className="space-y-2">
        {direction === "us-uk" ? (
          <Checkbox
            checked={oxford}
            label={strings.oxford}
            onChange={setOxford}
          />
        ) : null}
        <Checkbox
          checked={keepCapitalized}
          label={strings.keepCapitalized}
          onChange={setKeepCapitalized}
        />
      </div>
      <TextAreaField
        id="bas-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={
          direction === "uk-us" ? strings.placeholderUk : strings.placeholderUs
        }
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setText(direction === "uk-us" ? strings.sampleUk : strings.sampleUs)
          }
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {result ? (
        <Results
          direction={direction}
          onToggle={toggleKeep}
          result={result}
          strings={strings}
        />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
